/* eslint-disable react-compiler/react-compiler --
 * This component uses Formik's render prop pattern; several custom hooks are
 * called inside the render callback. React Compiler correctly flags this as
 * "hooks in function expressions", but the pattern predates this rule in this
 * file and refactoring the whole render prop into a subcomponent is out of
 * scope for the In-Person Assessment change set. Disable file-wide.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/styles";
import { Formik } from "formik";
import { DateCalendar } from "@mui/x-date-pickers";
import { Calendar as CalendarIcon } from "@phosphor-icons/react";
import { useHistory } from "react-router-dom";
import { useQuery } from "react-query";
import { format, isBefore, startOfDay, endOfDay, addMinutes } from "date-fns";
import { formatInTimeZone, zonedTimeToUtc } from "date-fns-tz";
import { useLocation } from "react-router-dom";

import * as selectors from "selectors";
import actions from "actions";
import { PageContainer } from "@answersnow/pkg-theme";
import {
  createCalendarCallEvents,
  createPrivateEventList,
} from "components/WeeklyCalendarSessions/CalendarUtils";
import { getAllClinicians } from "api/clinicians";
import { useSearchClients } from "hooks/useSearchClients";
import { useCalendarSessionsByDates } from "hooks/useCalendarSessionsByDates";
import { useGetHasInsuranceAuthorization } from "hooks/useGetHasInsuranceAuthorization";
import useIsClientVAMedicaid from "hooks/api/insurance/useIsClientVAMedicaid";
import { useGetAppointmentAvailability } from "hooks/useGetAppointmentAvailability";
import useGetNonVideoServiceTypes from "hooks/useGetNonVideoServiceTypes";
import { addMinutesToTime, getDateString, getISODateTime } from "utils/dateTimeUtils";
import { useGetFeatureFlag } from "hooks/useGetFeatureFlag";
import { useClientAvailability } from "components/ClientDetails/Sections/ClientAvailability/hooks";
import titleCaseToCamelCase from "utils/titleCaseToCamelCase";
import { useGlobalToast } from "components/GlobalToastProvider";
import ColorIndexManager from "utils/ColorIndexManager";
import useDebounce from "hooks/utils/useDebounce";
import { findCancelReasonByResponsible } from "@constants/cancelReasons";
import CancelSessionDialog from "../WeeklyCalendarSessions/CancelSessionDialog";
import useGetCancellationReasons from "hooks/api/appointments/useGetCancellationReasons";
import useEventFormSubmit from "./useEventFormSubmit";
import VideoCallSelections from "./FormRendering/VideoCallSelections";
import AvailabilityViewer from "./FormRendering/AvailabilityViewer";
import LoadingChangesDialog from "./LoadingChangesDialog";
import { LoadingScreen, useTimeOptions, formatRepeatDays } from "./EventSchedulingUtils";
import UpdateEventDialog from "./UpdateEventDialog";
import { mergeClientAvailability } from "./mergeClientAvailability";
import validationSchema from "./validationSchema";

const videoServiceTypeOptions = [
  { label: "Select", value: "" },
  { label: "Assessment", value: "ASSESSMENT", icon: <CalendarIcon /> },
  {
    label: "Caregiver Training",
    value: "CAREGIVER_TRAINING",
    icon: <CalendarIcon />,
  },
  { label: "Direct Therapy", value: "DIRECT_THERAPY", icon: <CalendarIcon /> },
  { label: "Orientation", value: "ORIENTATION", icon: <CalendarIcon /> },
  { label: "Treatment Plan Review", value: "TREATMENT_PLAN_REVIEW", icon: <CalendarIcon /> },
  { label: "Caregiver Mediated", value: "CAREGIVER_MEDIATED", icon: <CalendarIcon /> },
];

// "Assessment - In Person" is only exposed when the event type is "Off Platform Session"
// (formerly "Doxy session"). VA Medicaid contracts require in-person assessment appointments.
// The option is intentionally omitted from the AnswersNow Video Call flow.
const inPersonAssessmentServiceTypeOption = {
  label: "Assessment - In Person",
  value: "IN_PERSON_ASSESSMENT",
  icon: <CalendarIcon />,
};

const eventOptionsObject = {
  therapySession: { value: "therapy-session", label: "AnswersNow Video Call" },
  doxySession: { value: "doxy-session", label: "Off Platform Session" },
  otherServices: { value: "other-services", label: "Other Services" },
};

const eventOptions = [
  { label: "Select", value: "" },
  eventOptionsObject.therapySession,
  eventOptionsObject.doxySession,
  eventOptionsObject.otherServices,
];

export default function EventScheduling() {
  "use memo"; // will use react compiler
  const dispatch = useDispatch();
  const theme = useTheme();
  const history = useHistory();
  const { showToast } = useGlobalToast();

  // Color index manager for user schedule colors
  const colorIndexManager = useRef(new ColorIndexManager());

  const { id: userId } = useSelector(selectors.getUser);
  const userPermissions = useSelector(selectors.getUserPermissionsList);
  const scheduleCallLoading = useSelector(selectors.scheduleCallLoading);
  const scheduleCallSuccess = useSelector(selectors.scheduleCallSuccess);
  const timeOptions = useTimeOptions();
  const [selectedClinicians, setSelectedClinicians] = useState([]);
  const selectedClinician =
    selectedClinicians?.find((cl) => cl.isPrimary) ||
    selectedClinicians?.find((cl) => !cl.isPrimary);
  const [initialVideoClinicianLoaded, setInitialVideoClinicianLoaded] = useState(false);

  const [mergedTimeSlots, setMergedTimeSlots] = useState([]);
  const [updateEventDialogOpen, setUpdateEventDialogOpen] = useState(false);
  const [rescheduleAllInstances, setRescheduleAllInstances] = useState("false");
  const { data: cliniciansResponse } = useQuery("allClinicians", getAllClinicians, {
    refetchOnWindowFocus: false, // Disable refetching on window focus
  });
  const clinicians = cliniciansResponse?.data ?? [];
  const { search } = useLocation();
  const canSelectBCBA = userPermissions?.select_scheduled_clinician;

  const query = new URLSearchParams(search);
  const videoId = query.get("id");
  const isNonVideoEvent = query.get("isNonVideoEvent") === "true";
  const clientNameParam = query.get("name");
  const serviceTypeParam = query.get("serviceType");
  const isEditing = !!videoId;

  const selectedVideoChatInfo = useSelector(selectors.videoChatInfo);
  const videoChatInfo = isEditing ? selectedVideoChatInfo : null;
  const videoChatInfoLoading = useSelector(selectors.videoChatInfoLoading);
  const [cancelSessionOpen, setCancelSessionOpen] = useState(false);
  const [cancelReasonText, setCancelReasonText] = useState("");
  const [cancelAllInstances, setCancelAllInstances] = useState(false);
  const [responsibleForCancellation, setResponsibleForCancellation] = useState("");
  const [cancellationReasonId, setCancellationReasonId] = useState("");
  const [canceled, setCanceled] = useState(false);
  const {
    cancellationReasons,
    isLoading: cancellationReasonsLoading,
  } = useGetCancellationReasons();
  const {
    cancellationReasons: recurringCancellationReasons,
    isLoading: recurringCancellationReasonsLoading,
  } = useGetCancellationReasons(true);
  const featureFlagQuery = useGetFeatureFlag(userId, "scheduling_page");

  // The state of the built event modified via drag and drop
  const [moveBuiltEvent, setMoveBuiltEvent] = useState(null);

  const {
    onSubmit,
    onSubmitLoading,
    onSubmitSuccess,
    onCancelSubmit,
    onCancelSubmitLoading,
    onCancelSubmitSuccess,
    onCancelSubmitError,
  } = useEventFormSubmit({
    rescheduleAllInstances,
    selectedClinician,
    cancellationReasons,
    recurringCancellationReasons,
    secondaryClinicianUserId:
      selectedClinicians?.length > 1
        ? selectedClinicians?.find((cl) => !cl.isPrimary)?.user_id
        : null,
  });

  const stringToBoolean = (value) => {
    if (value && typeof value === "string") {
      if (value.toLowerCase() === "true") return true;
      if (value.toLowerCase() === "false") return false;
    }
    return value;
  };

  const onChangeCancelSelection = (e) => {
    const { value } = e.target;
    setCancelAllInstances(stringToBoolean(value));
    setCancellationReasonId("");
    setCancelReasonText("");
    setResponsibleForCancellation("");
  };
  const onChangeCancelReason = (e) => {
    const reasons = cancelAllInstances ? recurringCancellationReasons : cancellationReasons;
    const cancellationReason = (reasons || []).find(
      (reason: { id: string | number }) => String(reason.id) === String(e.target.value)
    );
    if (cancellationReason) {
      setCancellationReasonId(cancellationReason.id);
      setCancelReasonText(cancellationReason.displayLabel);
      setResponsibleForCancellation(cancellationReason.responsibleForCancellation);
      return;
    }

    setCancellationReasonId("");
    setCancelReasonText(e.target.value);
  };
  const onChangeResponsibleForCancellation = (e) => {
    const legacyReason = findCancelReasonByResponsible(e.target.value);
    setCancellationReasonId("");
    setResponsibleForCancellation(e.target.value);
    setCancelReasonText(
      legacyReason && !Array.isArray(legacyReason.cancelReasons)
        ? legacyReason.cancelReasons.label
        : ""
    );
  };
  const submitCancelSession = () => {
    onCancelSubmit({
      isNonVideoEvent,
      eventInfo: videoChatInfo,
      cancelAllInstances,
      message: cancelReasonText,
      responsibleForCancellation,
      cancellationReasonId,
    });
    setCanceled(true);
  };

  const onToggleCancelSession = () => {
    setCancelSessionOpen(!cancelSessionOpen);
    if (cancelSessionOpen) {
      setCancelReasonText("");
      setCancellationReasonId("");
      setResponsibleForCancellation("");
      setCancelAllInstances(false);
    }
  };

  useEffect(() => {
    dispatch(actions.setPageDetails({ pageName: "Schedule" }));

    if (isEditing) {
      dispatch(actions.getVideoChatInfo(videoId, isNonVideoEvent));
      dispatch(actions.getAssignableClinicians());
    }
  }, []);

  useEffect(() => {
    if (onSubmitSuccess) {
      history.goBack();
    }
  }, [onSubmitSuccess]);
  // If user has no permission to access the scheduling page, redirect to previous page
  useEffect(() => {
    const { data: schedulePageFeatureFlag, isLoading } = featureFlagQuery;
    if (userId && !isLoading && !schedulePageFeatureFlag) history.goBack();
  }, [userId, featureFlagQuery]);

  // Auto select clinician if user doesn't have permission to select
  useEffect(() => {
    if (!selectedClinicians?.length > 0 && userPermissions && !canSelectBCBA) {
      const clinician = clinicians.find((clinician) => clinician.clinician_user_id === userId);
      if (clinician) {
        setSelectedClinicians([
          {
            ...clinician,
            isPrimary: true,
          },
        ]);
      }
    }
  }, [userId, clinicians, userPermissions, selectedClinicians]);

  useEffect(() => {
    if (canSelectBCBA) dispatch(actions.getAssignableClinicians());
  }, [userPermissions]);

  // Effects to set initial states values when editing
  useEffect(() => {
    if (videoChatInfo && isEditing) {
      if (!userId) return;
      const ownEvent = videoChatInfo.clinician_user_id === userId;
      const hasSchedulingInfo =
        videoChatInfo.scheduled_date != null && videoChatInfo.call_duration != null;
      const isExpired = hasSchedulingInfo
        ? isBefore(
            addMinutes(new Date(videoChatInfo.scheduled_date), videoChatInfo.call_duration),
            new Date()
          )
        : false;
      if (
        !hasSchedulingInfo ||
        videoChatInfo.canceled ||
        isExpired ||
        (!canSelectBCBA && !ownEvent) ||
        videoChatInfo.no_show ||
        videoChatInfo.start_date ||
        videoChatInfo.not_started
      ) {
        history.goBack(); // Don't allow editing, just go back
      }
    }
  }, [videoChatInfo, userId]);

  const nonVideoEventServiceTypes = useGetNonVideoServiceTypes();
  const nonVideoEventServiceTypeOptions = nonVideoEventServiceTypes.map(({ id, description }) => ({
    label: description,
    value: id,
  }));

  const serviceTypeLookup = useMemo(
    () =>
      [
        ...videoServiceTypeOptions,
        inPersonAssessmentServiceTypeOption,
        ...nonVideoEventServiceTypeOptions,
      ].reduce((acc, item) => {
        acc[item.value] = item.label;
        return acc;
      }, {}),
    [nonVideoEventServiceTypeOptions]
  );

  if (videoChatInfoLoading || (!videoChatInfo && isEditing)) return <LoadingScreen />;
  let initialValues = {
    event: "",
    clientInputValue: clientNameParam || "",
    selectedClient: null,
    selectedTeamMember: null,
    serviceType: serviceTypeParam || "",
    bcba: "",
    eventDate: new Date(),
    startTime: "",
    duration: 60,
    timeUnit: "",
    isRepeating: false,
    isTentative: false,
    timeInterval: 1,
    repeatEndType: "",
    repeatUntilDate: null,
    repeatDays: [],
  };

  if (isEditing) {
    initialValues = {
      ...initialValues,
      clientInputValue: `${videoChatInfo?.name} ${videoChatInfo?.child_last_name}`,
      event: isNonVideoEvent
        ? eventOptionsObject.otherServices.value
        : videoChatInfo.off_platform
          ? "doxy-session"
          : "therapy-session",
      serviceType: isNonVideoEvent
        ? parseFloat(videoChatInfo.billing_type)
        : videoChatInfo.billing_type,
      bcba: videoChatInfo.clinician_user_id,
      eventDate: videoChatInfo.scheduled_date ? new Date(videoChatInfo.scheduled_date) : null,
      startTime: videoChatInfo.scheduled_date
        ? format(new Date(videoChatInfo.scheduled_date), "h:mm a")
        : "",
      duration: videoChatInfo.call_duration,
      timeUnit: videoChatInfo.time_unit,
      isRepeating: videoChatInfo.is_recurring,
      timeInterval: videoChatInfo.time_interval,
      repeatEndType: videoChatInfo.repeat_type,
      repeatUntilDate: videoChatInfo.repeat_until_date
        ? new Date(videoChatInfo.repeat_until_date)
        : null,
      repeatDays: formatRepeatDays(videoChatInfo.repeat_days),
      isTentative: videoChatInfo.is_tentative,
    };
  }

  return (
    <PageContainer horizontalPadding={false} fixHeight>
      <Formik
        initialValues={initialValues}
        validationSchema={() =>
          validationSchema({
            canSelectBCBA,
            eventDate: initialValues.eventDate,
          })
        }
        onSubmit={(values) => onSubmit(values, videoChatInfo)}
      >
        {({ values, handleChange, setFieldValue, handleSubmit, validateForm, setTouched }) => {
          const isOtherServicesEvent = values.event === eventOptionsObject.otherServices.value;
          const debouncedClientInputValue = useDebounce(values.clientInputValue, 500);

          // Determine service type options based on event type.
          // "Assessment - In Person" is only appended for Off Platform Session
          // events; the AnswersNow Video Call flow keeps the legacy list.
          const isOffPlatformEvent = values.event === eventOptionsObject.doxySession.value;
          const serviceTypeOptions = useMemo(
            () =>
              isOtherServicesEvent
                ? nonVideoEventServiceTypeOptions
                : isOffPlatformEvent
                  ? [...videoServiceTypeOptions, inPersonAssessmentServiceTypeOption]
                  : videoServiceTypeOptions,
            [values.event, nonVideoEventServiceTypeOptions, isOffPlatformEvent]
          );
          // Fetch client availability data
          const dayOfWeek = format(values?.eventDate, "eeee").toLowerCase();
          const [clientAvailabilityData, setClientAvailabilityData] = useState([]);
          const { refetch } = useClientAvailability({
            clientId: values?.selectedClient?.id,
            serviceType:
              typeof values?.serviceType === "string"
                ? titleCaseToCamelCase(values?.serviceType)
                : values?.serviceType ?? "directTherapy", // Send integer or default value
            dayOfWeek,
            enabled: !!values?.selectedTeamMember?.userId && !!values?.eventDate,
            onSuccess: ({ data }) =>
              setClientAvailabilityData(data?.services[0]?.availability[dayOfWeek]),
          });

          useEffect(() => {
            if (videoChatInfo && !selectedClinicians && clinicians.length > 0) {
              // current user
              setSelectedClinicians([
                {
                  ...clinicians.find((clinician) => clinician.clinician_user_id === userId),
                  isPrimary: true,
                },
              ]);
            }
          }, [videoChatInfo, clinicians, values.selectedTeamMember?.userId]);

          // Refetch client availability data when client ID, eventDate, or serviceType changes
          useEffect(() => {
            if (values?.selectedTeamMember?.userId && values?.eventDate && values?.serviceType) {
              refetch();
            }
          }, [values?.selectedTeamMember?.userId, values?.eventDate, values?.serviceType]);

          // Load clinicians based on video chat info
          useEffect(() => {
            if (
              !initialVideoClinicianLoaded &&
              videoChatInfo &&
              clinicians &&
              !selectedClinicians?.length
            ) {
              // Filter and map clinicians to include isPrimary property
              const loadedInClinicians = clinicians.filter((clinician) =>
                [
                  videoChatInfo.clinician_user_id,
                  videoChatInfo.secondary_clinician_user_id,
                ].includes(clinician.clinician_user_id)
              );
              // Sort the loadedInClinicians to put primary clinician first
              loadedInClinicians.sort((a, b) => b.isPrimary - a.isPrimary);

              if (loadedInClinicians.length > 0) {
                setInitialVideoClinicianLoaded(true);
                setSelectedClinicians(loadedInClinicians);

                // Collect clinician user IDs into a comma-separated string
                const clinicianUserIds = loadedInClinicians
                  .map((clinician) => clinician.user_id)
                  .join(",");

                handleChange({
                  target: {
                    name: "bcba",
                    value: clinicianUserIds,
                  },
                });
              }
            }
          }, [videoChatInfo, clinicians, selectedClinicians, initialVideoClinicianLoaded]);

          // Manually validates the form and returns any errors
          const manuallyValidateForm = async () => {
            const errors = await validateForm();
            if (Object.keys(errors).length > 0) {
              setTouched(
                Object.keys(values).reduce((acc, curr) => {
                  acc[curr] = true;
                  return acc;
                }, {})
              );
              return errors;
            }
          };

          // Parse the date based on the selected clinician's timezone
          const parsedDate =
            selectedClinicians?.length > 0
              ? formatInTimeZone(values?.eventDate, selectedClinicians[0]?.timezone, "yyyy-MM-dd")
              : null;

          // Fetch clinician availability
          const { data: clinicianAvailability, isLoading: loadingClinicianAvailability } =
            useGetAppointmentAvailability(
              selectedClinicians?.length > 0
                ? selectedClinicians.map((clinician) => clinician.clinician_user_id)
                : [],
              values.duration,
              parsedDate,
              parsedDate
            );

          // Merge availability data
          useEffect(() => {
            if (
              !loadingClinicianAvailability &&
              clinicianAvailability?.data &&
              selectedClinicians?.length > 0
            ) {
              const mergedSlots = {};
              Object.keys(clinicianAvailability.data).forEach((id) => {
                const timezone = selectedClinicians[0].timezone;
                mergedSlots[id] = clinicianAvailability.data[id]
                  .filter((slot) => slot.startDateTime)
                  .map((slot) => zonedTimeToUtc(slot.startDateTime, timezone).toISOString());
              });
              setMergedTimeSlots(mergedSlots);
            }
          }, [clinicianAvailability?.data, loadingClinicianAvailability, selectedClinicians]);

          // Fetch calendar sessions by dates
          const { data: response } = useCalendarSessionsByDates({
            startDate: startOfDay(values.eventDate).toISOString(),
            endDate: endOfDay(values.eventDate).toISOString(),
            userIdList: [
              ...(selectedClinicians?.length > 0
                ? selectedClinicians.map((clinician) => clinician.clinician_user_id)
                : []),
              values.selectedTeamMember?.userId,
            ],
          });

          // Process calendar events and non-working hours
          const privateEventsList = createPrivateEventList(response?.data.googleCalendarEvents);
          const callList = createCalendarCallEvents(
            response?.data.calendarSessions,
            serviceTypeLookup
          );

          // Combine all events
          const allEvents = [...callList, ...privateEventsList];

          /*
            Create an event builder (a preview of the event to be scheduled) based on form values
            and add it to the list of events to be displayed on the calendar.
          **/
          const canBuildEvent =
            !isEditing &&
            values.selectedClient?.id &&
            selectedClinicians?.length > 0 &&
            values.eventDate &&
            values.startTime &&
            values.duration;

          if (canBuildEvent) {
            const eventDate = getDateString(values.eventDate);
            const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const start = getISODateTime(eventDate, values.startTime, localTimezone);
            const end = getISODateTime(
              eventDate,
              addMinutesToTime(
                values.startTime,
                values.duration,
                localTimezone,
                values.eventDate,
                localTimezone
              ),
              localTimezone
            );

            const eventBuilder = {
              id: 0,
              call_type: "BUILDING",
              is_tentative: values.isTentative,
              call_duration: values.duration,
              client_id: values.selectedClient?.id ?? null,
              client_name: values.selectedClient?.name ?? null,
              clinician_id: selectedClinician?.clinician_id ?? null,
              clinician_name: selectedClinician?.name ?? null,
              clinician_image_url: selectedClinician?.image_url ?? null,
              start: new Date(start),
              end: new Date(end),
              title: values?.serviceType ? serviceTypeLookup[values.serviceType] : "Direct Therapy",
              team_member_name: values.selectedTeamMember?.name,
              team_member_image_url: values.selectedTeamMember?.image_url,
            };

            // Add the event builder to the list of events
            allEvents.push(eventBuilder);
          }

          const nonWorkingHours = response?.data.nonWorkingHours;

          // Fetch client list
          const searchClientsInput = {
            userId: userId,
            inputValue: debouncedClientInputValue,
            activeOnly: true,
            isPrimaryOnly: true,
            includeTeam: true,
            exactMatch: false,
            onlyAccessibleClients: true,
          };

          const { data: clientsResponse } = useSearchClients(
            userId,
            searchClientsInput.inputValue,
            searchClientsInput.activeOnly,
            searchClientsInput.isPrimaryOnly,
            searchClientsInput.includeTeam,
            searchClientsInput.exactMatch,
            searchClientsInput.onlyAccessibleClients
          );

          const clientList = clientsResponse?.data ?? [];

          // Reset service type if event changes and the current selection is
          // no longer valid for the newly-selected event type. Off Platform
          // Session now exposes its own service type options (previously it
          // had none), so we no longer force-clear on that event.
          useEffect(() => {
            if (
              values.serviceType &&
              !serviceTypeOptions.some(({ value }) => value === values.serviceType)
            ) {
              setFieldValue("serviceType", "");
            }
          }, [values.event, serviceTypeOptions, values.serviceType]);

          // Handle scheduling success or cancellation
          useEffect(() => {
            if (scheduleCallSuccess || onCancelSubmitSuccess) {
              if (canceled && !cancelAllInstances) {
                showToast({ message: "Event successfully canceled" });
              } else if (canceled && cancelAllInstances) {
                showToast({
                  message: "This event and future sessions have been successfully canceled",
                });
              } else if (isEditing) {
                showToast({ message: "Event changes were successfully updated" });
              } else {
                showToast({ message: "Event scheduled successfully" });
              }

              // Close the cancel dialog if it's open and this is a cancellation
              if (canceled && cancelSessionOpen) {
                setCancelSessionOpen(false);
              }

              setUpdateEventDialogOpen(false);
              history.goBack();
              dispatch(actions.resetScheduleVideoCallState());
            } else if (onCancelSubmitError) {
              showToast({ message: "Whoops! Something went wrong", errorState: true });
            }
          }, [
            scheduleCallSuccess,
            onCancelSubmitSuccess,
            onCancelSubmitError,
            canceled,
            cancelSessionOpen,
          ]);

          // Handle selecting days for repeating events
          const handleSelectDay = (day) => {
            const { repeatDays } = values;
            if (repeatDays.includes(day)) {
              setFieldValue(
                "repeatDays",
                repeatDays.filter((d) => d !== day)
              );
            } else {
              setFieldValue(
                "repeatDays",
                [...repeatDays, day].sort((a, b) => a - b)
              );
            }
          };

          // Get the color for the selected day
          const getSelectedDayColor = (day) => {
            return values.repeatDays.includes(day) ? "primary" : "secondary";
          };

          // Fetch insurance authorization status
          const {
            data: insuranceAuthorizationResponse,
            isLoading: isLoadingInsuranceAuthorization,
          } = useGetHasInsuranceAuthorization(values.selectedClient?.id);
          const hasInsuranceAuthorization =
            insuranceAuthorizationResponse?.data?.hasInsuranceAuthorization ?? false;

          // For the "Assessment - In Person" service type we check whether the
          // client has active VA Medicaid coverage (primary or secondary). When
          // false, VideoCallSelections renders an inline warning so the scheduler
          // can double-check before creating the appointment. This is a
          // client-side soft warning only — it does not block submission.
          const { isVAMedicaid: isClientVAMedicaid, isLoading: isClientVAMedicaidLoading } =
            useIsClientVAMedicaid(values.selectedClient?.id);
          const showInPersonAssessmentPayerWarning =
            values.serviceType === "IN_PERSON_ASSESSMENT" &&
            !!values.selectedClient?.id &&
            !isClientVAMedicaidLoading &&
            !isClientVAMedicaid;

          // Update repeat end type based on insurance authorization status
          useEffect(() => {
            if (
              insuranceAuthorizationResponse &&
              !hasInsuranceAuthorization &&
              !isLoadingInsuranceAuthorization &&
              values.repeatEndType === "current_auth_period" &&
              !isEditing
            ) {
              setFieldValue("repeatEndType", "");
            }
          }, [insuranceAuthorizationResponse]);

          const [clinicianOptions, setClinicianOptions] = useState([]);

          return (
            <Stack direction="row" height="100%">
              <VideoCallSelections
                isEditing={isEditing}
                isOtherServicesEvent={isOtherServicesEvent}
                eventOptions={eventOptions}
                eventOptionsObject={eventOptionsObject}
                clientList={clientList}
                serviceTypeLookup={serviceTypeLookup}
                serviceTypeOptions={serviceTypeOptions}
                canSelectBCBA={canSelectBCBA}
                clinicians={clinicians}
                selectedClinicians={selectedClinicians}
                setSelectedClinicians={setSelectedClinicians}
                setClinicianOptions={setClinicianOptions}
                clinicianOptions={clinicianOptions}
                timeOptions={timeOptions}
                scheduleCallLoading={scheduleCallLoading}
                onSubmitLoading={onSubmitLoading}
                history={history}
                manuallyValidateForm={manuallyValidateForm}
                setUpdateEventDialogOpen={setUpdateEventDialogOpen}
                handleSubmit={handleSubmit}
                setCancelSessionOpen={setCancelSessionOpen}
                hasInsuranceAuthorization={hasInsuranceAuthorization}
                showInPersonAssessmentPayerWarning={showInPersonAssessmentPayerWarning}
                handleSelectDay={handleSelectDay}
                getSelectedDayColor={getSelectedDayColor}
                videoChatInfo={videoChatInfo}
                colorManager={colorIndexManager.current}
                moveBuiltEvent={moveBuiltEvent}
              />

              <Stack gap={theme.spacing(2)} padding="44px">
                <Typography paddingLeft={theme.spacing(7)} variant="subtitle1">
                  Select a date and time
                </Typography>
                <DateCalendar
                  name="eventDate"
                  value={values.eventDate}
                  disablePast
                  onChange={(value) => setFieldValue("eventDate", value, true)}
                />
              </Stack>

              <AvailabilityViewer
                values={values}
                selectedClinicians={selectedClinicians}
                setFieldValue={setFieldValue}
                allEvents={allEvents}
                nonWorkingHours={nonWorkingHours}
                mergedTimeSlots={mergedTimeSlots}
                loadingClinicianAvailability={loadingClinicianAvailability}
                clientAvailability={mergeClientAvailability(clientAvailabilityData?.ranges)}
                colorManager={colorIndexManager.current}
                onChangeBuiltEvent={setMoveBuiltEvent}
              />
              {values.isRepeating && (
                <LoadingChangesDialog
                  open={scheduleCallLoading || onSubmitLoading}
                  title={isEditing ? "Updating your event" : "Creating your event"}
                  description="We're adding the final touches. This may take a few moments."
                />
              )}

              <UpdateEventDialog
                open={updateEventDialogOpen}
                onClose={() => setUpdateEventDialogOpen(false)}
                rescheduleAllInstances={rescheduleAllInstances}
                setRescheduleAllInstances={setRescheduleAllInstances}
                showRecurringOptions={values.isRepeating}
                isTentative={values.isTentative}
              />
              <CancelSessionDialog
                sessionDetails={videoChatInfo}
                open={cancelSessionOpen}
                cancelReasonText={cancelReasonText}
                cancellationReasonId={cancellationReasonId}
                responsibleForCancellation={responsibleForCancellation}
                closeDialog={onToggleCancelSession}
                onChangeCancelReason={onChangeCancelReason}
                submitCancelSession={submitCancelSession}
                onChangeResponsibleForCancellation={onChangeResponsibleForCancellation}
                cancelAllInstances={cancelAllInstances}
                onChangeCancelSelection={onChangeCancelSelection}
                loading={onCancelSubmitLoading}
                cancellationReasons={cancellationReasons}
                recurringCancellationReasons={recurringCancellationReasons}
                cancellationReasonsLoading={cancellationReasonsLoading}
                recurringCancellationReasonsLoading={recurringCancellationReasonsLoading}
              />
            </Stack>
          );
        }}
      </Formik>
    </PageContainer>
  );
}
